import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

/**
 * Edits the onset date of one ICD-10 code, opened by double-clicking its chip.
 *
 * The date is a clinical and a billing fact — when the condition was documented, not when
 * somebody typed it in. The AI path takes it from the note it quoted; this is the manual
 * equivalent, and the two must produce the same shape or the column is unreportable.
 *
 * ⚠ EMITS 'YYYY-MM-DD', NEVER A Date. `MatDatepicker` hands back a `Date`, and a local calendar
 * day in SAST (UTC+2) serialises through `JSON.stringify` to the PREVIOUS day at 22:00Z. Sending
 * that would move every date back one day. The server normalises defensively too — see
 * `utils/icdDateUtils.js` — but a client that sends the right thing is not relying on it.
 */
@Component({
  selector: 'app-icd-code-date-dialog',
  templateUrl: './icd-code-date-dialog.component.html',
  styleUrls: ['./icd-code-date-dialog.component.scss']
})
export class IcdCodeDateDialogComponent {

  code: string;
  description: string;
  /** Bound to the picker. A Date because Material needs one; converted back on save. */
  selected: Date | null = null;

  constructor(
    private dialogRef: MatDialogRef<IcdCodeDateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.code = data?.code || '';
    this.description = data?.description || '';
    this.selected = this.toDate(data?.onsetDate);
  }

  /**
   * 'YYYY-MM-DD' -> a LOCAL midnight Date.
   *
   * Built from parts rather than `new Date('2026-05-14')`, which the spec parses as UTC — in
   * SAST that renders as the 14th but in any negative offset it renders as the 13th, so the
   * picker would open on the wrong day for half the world.
   */
  private toDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    if (!match) return null;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  /** A Date -> 'YYYY-MM-DD', read off the LOCAL calendar. Never `toISOString()`. */
  private toIsoDay(date: Date | null): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  clear(): void {
    // Clearing is legitimate: "we do not know when this began" is a truthful answer, and a
    // forced date would be a guess sitting on a billable record.
    this.dialogRef.close({ onsetDate: null });
  }

  save(): void {
    this.dialogRef.close({ onsetDate: this.toIsoDay(this.selected) });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
